/**
 * productsEngineConfig.tsx — silnik formatek (unifikacja-formatek): config
 * kartoteki towarów (F18 T18.5 migracja na `RecursiveFormRenderer` +
 * `useKartotekaRenderField`).
 *
 * Towary są CAŁKOWICIE read-only (brak POST/PATCH — asortyment idzie
 * jednokierunkowo z Symfonii, sync_products). Ten config jest więc
 * konsumowany przez `ProductDetail.tsx` z `editable: false`/`isCreate: false`
 * na sztywno w całym drzewie — żaden `renderer` tutaj nigdy nie odpala
 * zapisu, ale kontrakt silnika i tak wymaga runtime (`onChange`/`onExtChange`)
 * — strona przekazuje no-opy.
 *
 * 17 pól natywnych (delegacja do `ExtFieldValueInput` przez silnik — brak
 * `renderer` w configu) + `product_type` z nadpisaniem: pole jest seedowane
 * jako `fieldType: 'select'` (patrz `seed-system-modules.ts`, moduł
 * `products`), ale BEZ `options` w seedzie — delegacja do
 * `ExtFieldValueInput` renderowałaby pusty dropdown. Enum NIE jest wymuszony
 * w DB (kolumna `product_type` to zwykły `varchar`), więc lista jest na
 * sztywno (mirror `StatusSelect` z `documentCoreRenderers.tsx`) — zawsze
 * `disabled` (towar nigdy nie jest edytowalny).
 *
 * Kolejność `PRODUCT_CORE_FIELD_SLUGS` = kolejność `coreFields` w seedzie
 * (`products.coreFields`) — top-up strony syntetyzuje z niej zakładkę 'data'
 * gdy tenant nie ma jeszcze aktywnego layoutu.
 */

import { Dropdown } from '@/components/Dropdown';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderer,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';

/**
 * Stan formularza towaru — DTO `/api/products/:id` minus `id`/`externalId`
 * (`externalId` nie jest w `coreFieldSlugs`/`fields` — nigdy nie jest
 * renderowany przez silnik, więc nie ma czego trzymać w stanie formularza).
 */
export interface ProductFormData {
  code: string | null;
  name: string;
  productType: string;
  barcode: string | null;
  pkwiu: string | null;
  cn: string | null;
  fiscalName: string | null;
  unit: string | null;
  vatRate: string | null;
  currencyCode: string;
  purchasePrice: string | null;
  basePrice: string | null;
  weight: string | null;
  category: string | null;
  /**
   * Kategoria kartoteki Towary — pojedynczy wybór z `record_categories` (kartoteka_key='products',
   * migracja 0131). NIE mylić z legacy `category` (kod rodzaju z Symfonii). Jedyne EDYTOWALNE pole
   * towaru — zapisywane inline przez PATCH /api/products/:id (patrz `ProductEngineExtra`).
   */
  categoryId: string | null;
  catalogPath: string | null;
  description: string | null;
  isActive: boolean;
  source: string;
}

/**
 * Nieprzezroczysty stan runtime towaru (TExtra silnika). Towar jest read-only z synca Symfonii,
 * ALE kategoria kartoteki to nasza lokalna oś — edytowalna niezależnie. Strona (`ProductDetail`)
 * wstrzykuje tu zapis inline (własny PATCH), bo globalny `editable` silnika jest `false`.
 */
export interface ProductEngineExtra {
  /** Zapis kategorii inline (PATCH /api/products/:id { categoryId }). */
  saveCategory: (next: string | null) => void;
  /** Trwa zapis kategorii — picker zablokowany na czas requestu. */
  savingCategory: boolean;
  /**
   * Towar zablokowany na czas zadania przycisku SQL — picker zablokowany (serwer odrzuca w tym czasie
   * zapis kategorii 409 `RECORD_LOCKED_BY_RUN`).
   */
  categoryLocked: boolean;
}

/** Enum typu towaru — NIE jest wymuszony w DB (kolumna `varchar`), lista twarda. */
const PRODUCT_TYPES = ['good', 'service', 'set'] as const;

/** Custom widget (mirror `StatusSelect`) — zawsze `disabled` (towary są read-only). */
const productTypeRenderer: FieldRenderer<ProductFormData, ProductEngineExtra> = ({
  field,
  form,
  testIdPrefix,
  t,
}) => {
  // product_type to zwykły varchar (Symfonia), NIE jest wymuszony przez enum w DB —
  // wartość spoza PRODUCT_TYPES musi i tak zostać pokazana (deleted ProductDataTab
  // pokazywał surową wartość), inaczej disabled <select> renderowałby się puste i
  // user tracił realną wartość rekordu.
  const isKnownType = (PRODUCT_TYPES as readonly string[]).includes(form.productType);
  return (
    <Field label={field.name}>
      <Dropdown
        value={form.productType}
        disabled
        data-testid={`${testIdPrefix}-productType`}
        options={[
          ...PRODUCT_TYPES.map((v) => ({
            value: v,
            label: t(`tenant.products.type.${v}`, { defaultValue: v }),
          })),
          // Wartość spoza znanego enuma (varchar z Symfonii) — dokładana jako opcja,
          // żeby trigger pokazał surową wartość rekordu zamiast pustki.
          ...(form.productType && !isKnownType
            ? [{ value: form.productType, label: form.productType }]
            : []),
        ]}
      />
    </Field>
  );
};

/**
 * Kategoria kartoteki (`record_categories`, kartoteka_key='products') — pojedynczy wybór.
 * EDYTOWALNA mimo że reszta towaru jest read-only: `RecordCategoryPicker` w trybie editable,
 * zapis inline przez `extra.saveCategory` (globalny `editable` silnika jest `false` i nie
 * dotyczy tego pola). Migracja 0130 — patrz `apps/api/lib/records/categories.ts`.
 */
const categoryRenderer: FieldRenderer<ProductFormData, ProductEngineExtra> = ({
  form,
  extra,
  testIdPrefix,
}) => (
  <RecordCategoryPicker
    kartotekaKey="products"
    value={form.categoryId}
    onChange={(next) => extra?.saveCategory(next)}
    editable={!!extra && !extra.savingCategory && !extra.categoryLocked}
    testId={`${testIdPrefix}-category`}
  />
);

const fields: Record<string, CoreFieldConfig<ProductFormData, ProductEngineExtra>> = {
  code: { stateKey: 'code', fieldType: 'varchar' },
  name: { stateKey: 'name', fieldType: 'varchar' },
  product_type: { stateKey: 'productType', fieldType: 'select', renderer: productTypeRenderer },
  barcode: { stateKey: 'barcode', fieldType: 'varchar' },
  pkwiu: { stateKey: 'pkwiu', fieldType: 'varchar' },
  cn: { stateKey: 'cn', fieldType: 'varchar' },
  fiscal_name: { stateKey: 'fiscalName', fieldType: 'varchar' },
  vat_rate: { stateKey: 'vatRate', fieldType: 'varchar' },
  currency_code: { stateKey: 'currencyCode', fieldType: 'varchar' },
  purchase_price: { stateKey: 'purchasePrice', fieldType: 'numeric' },
  base_price: { stateKey: 'basePrice', fieldType: 'numeric' },
  unit: { stateKey: 'unit', fieldType: 'varchar' },
  category: { stateKey: 'category', fieldType: 'varchar' },
  category_id: { stateKey: 'categoryId', fieldType: 'select', renderer: categoryRenderer },
  catalog_path: { stateKey: 'catalogPath', fieldType: 'varchar' },
  weight: { stateKey: 'weight', fieldType: 'numeric' },
  is_active: { stateKey: 'isActive', fieldType: 'boolean' },
  source: { stateKey: 'source', fieldType: 'varchar' },
  description: { stateKey: 'description', fieldType: 'text', colSpan: 2 },
};

/** Slugi core w kolejności seeda (`seed-system-modules.ts` → `products.coreFields`). */
export const PRODUCT_CORE_FIELD_SLUGS = [
  'code',
  'name',
  'product_type',
  'barcode',
  'pkwiu',
  'cn',
  'fiscal_name',
  'vat_rate',
  'currency_code',
  'purchase_price',
  'base_price',
  'unit',
  'category',
  'category_id',
  'catalog_path',
  'weight',
  'is_active',
  'source',
  'description',
];

export const productsEngineConfig: KartotekaEngineConfig<ProductFormData, ProductEngineExtra> = {
  moduleSlug: 'products',
  testIdPrefix: 'product',
  coreFieldSlugs: PRODUCT_CORE_FIELD_SLUGS,
  fields,
};
